import { isNumber, parseInt } from '../../utils/lodash-min';
import { Graphic } from './graphic';
import { MemeText } from './meme-text';
import { Dimension, Rectangle, TextAlignTypes } from './meme.types';

export class CanvasManager {
    private parent: HTMLDivElement;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private textWidthMap = new Map<string, number>();

    constructor(canvas: HTMLCanvasElement, width: number, height: number) {
        this.canvas = canvas;
        this.updateCanvasDimension(width, height);
        this.ctx = canvas.getContext('2d') as any;
    }

    updateCanvasDimension(width: number, height: number) {
        this.parent = document.getElementsByClassName('canvas-container')[0] as HTMLDivElement;
        this.parent.style.width = width + 'px';
        this.parent.style.height = height + 'px';
        this.canvas.width = width;
        this.canvas.height = height;
    }

    getContext() {
        return this.ctx;
    }

    getCanvas() {
        return this.canvas;
    }

    drawImage(img: HTMLImageElement, rect: Rectangle);
    drawImage(img: HTMLImageElement, x: number, y: number, width: number, height: number);
    drawImage(img: HTMLImageElement, ...args) {
        if (isNumber(args[0])) {
            this.ctx.drawImage(img, args[0], args[1], args[2], args[3]);
        } else {
            this.ctx.drawImage(img, args[0].x, args[0].y, args[0].width, args[0].height);
        }
    }

    drawRect(rect: Rectangle, color: string) {
        this.ctx.fillStyle = color;
        this.ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    /*
    writeText(text: MemeText) {
        this.saveWords(text);
        this.breakSentences(text);
    }
    */

    public drawText(text: MemeText) {
        this.updateTextBoundingRectangle(text);

        const maxMaxFontSize = parseInt(text.getFontSize() + '', 10);
        const fontGap = parseInt(text.getFontGap() + '', 10);
        this.ctx.font = maxMaxFontSize + 'pt ' + text.fontFamily;
        this.ctx.lineJoin = 'round';
        this.ctx.fillStyle = text.fontColor;
        this.ctx.textAlign = text.textAlign;
        if (text.hasLineWidth) {
            this.ctx.lineWidth = 2;
            this.ctx.strokeStyle = 'black';
        }
        // console.log(text.text, text.location.x, text.location.y);
        const sentences = this.breakSentences(text);
        if (sentences) {
            let location = text.getLocation().y + maxMaxFontSize;
            let offset = 0;
            if (text.textAlign === TextAlignTypes.CENTER) {
                offset = text.boundingRectangle.width / 2;
            } else if (text.textAlign === TextAlignTypes.RIGHT) {
                offset = text.boundingRectangle.width;
            }

            sentences.forEach((sentence) => {
                this.ctx.fillText(sentence, text.getLocation().x + offset, location + text.getTopOffset());
                if (text.hasLineWidth) {
                    this.ctx.strokeText(sentence, text.getLocation().x + offset, location + text.getTopOffset());
                }
                location += maxMaxFontSize + fontGap;
            });
        }
    }

    public updateTextBoundingRectangle(text: MemeText) {
        this.updateTextSizeToFitContainer(text);
        const c = this.getTextBoundingRectangle(text);
        // this.ctx.fillStyle = 'red';
        // this.ctx.fillRect(c.x, c.y, c.width, c.height);
        text.setBoundingRect({
            x: c.x, y: c.y, width: c.width, height: c.height,
        } as Rectangle);
    }

    public updateGraphicBoundingRectangle(graphic: Graphic) {
        const location = graphic.getLocation();
        const rect = graphic.getBoundingRect();
        graphic.setBoundingRect({
            x: location.x,
            y: location.y,
            width: location.width,
            height: rect.height * location.width / rect.width,
        });
    }

    private getTextBoundingRectangle(text: MemeText) {
        const rect: Rectangle = {} as any;
        const maxMaxFontSize = parseInt(text.getFontSize() + '', 10);
        const fontGap = parseInt(text.getFontGap() + '', 10);
        this.ctx.font = maxMaxFontSize + 'pt ' + text.fontFamily;
        this.ctx.lineJoin = 'round';
        this.ctx.fillStyle = text.fontColor;
        this.ctx.textAlign = text.textAlign;
        if (text.hasLineWidth) {
            this.ctx.lineWidth = 2;
            this.ctx.strokeStyle = 'black';
        }
        // console.log(text.text, text.location.x, text.location.y);
        const sentences = this.breakSentences(text);
        let location = 0;
        if (sentences) {
            sentences.forEach((sentence) => {
                // this.ctx.fillText(sentence, text.location.x, location);
                // this.ctx.strokeText(sentence, text.location.x, location);
                location += maxMaxFontSize + fontGap;
            });
        }

        rect.x = text.getLocation().x;
        rect.y = text.getLocation().y;
        rect.width = text.getLocation().width;
        rect.height = 10 + location - fontGap;

        return rect;
    }

    getFontSizeToFitContainerWidth(text: MemeText) {
        const maxMaxFontSize = parseInt(text.getMaxFontSize() + '', 10);
        this.ctx.lineJoin = 'round';
        this.ctx.fillStyle = text.fontColor;
        this.ctx.textAlign = text.textAlign;
        if (text.hasLineWidth) {
            this.ctx.lineWidth = 2;
            this.ctx.strokeStyle = 'black';
        }
        // console.log(text.text, text.location.x, text.location.y);
        const sentences = this.breakSentences(text);
        if (sentences) {
            for (let fs = maxMaxFontSize; fs > 1; fs--) {
                this.ctx.font = fs + 'pt ' + text.fontFamily;
                let sizeExceeded = false;
                for (const sentence of sentences) {
                    const requiredWidth = this.ctx.measureText(sentence).width;
                    if (text.getLocation().width < requiredWidth) {
                        sizeExceeded = true;
                    }
                }
                if (!sizeExceeded) {
                    return fs;
                }
            }
        }

        return text.getMaxFontSize();
    }

    private updateTextSizeToFitContainer(text: MemeText) {
        const targetFontSize = this.getFontSizeToFitContainerWidth(text);
        text.setFontSize(targetFontSize);
    }
    /*
        saveWords(memeText: MemeText) {
            let text = memeText.text;
            text = text.replace('&#13;', '\n');
            const texts = text.split(/ /ig);
            if (this.textWidthMap.get(' ')) {
                this.textWidthMap.set(' ', this.ctx.measureText(' ').width);
            }
            texts.forEach(t => {
                if (this.textWidthMap.get(t)) {
                    this.textWidthMap.set(t, this.ctx.measureText(t).width);
                }
            });
        }
        */
    breakSentences(memeText: MemeText) {
        return memeText.getText().split(/\n/);
    }

    getBounds(): Dimension {
        return {
            width: this.canvas.width,
            height: this.canvas.height,
        };
    }

    getContainerBoundingRect(): Rectangle {
        const rect = this.parent.getBoundingClientRect();
        return {
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
        };
    }

    setBounds(dim: Dimension): void {
        this.canvas.width = dim.width;
        this.canvas.height = dim.height;
    }

    getCanvasWidth() {
        return this.getBounds().width;
    }

    downloadImage() {
        this.canvas.toDataURL('image/jpeg', 1.0);
    }
}
